import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus, Edit2, Trash2, HelpCircle, Info, GripVertical } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
function SortableSettingsRow({ setting, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: setting.settings_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-stone-100 hover:bg-stone-50">
      <td className="w-8 py-3 px-2">
        <button {...attributes} {...listeners} className="cursor-grab hover:text-green-700">
          <GripVertical className="h-4 w-4 text-stone-400" />
        </button>
      </td>
      <td className="py-3 px-2">{setting.competition_type} Competitions</td>
      <td className="py-3 px-2">
        <div className="flex items-center justify-end gap-2">
          <button 
            onClick={() => onEdit(setting)} 
            className="action-icon"
            data-testid={`edit-settings-${setting.settings_id}`}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button 
            onClick={() => onDelete(setting)} 
            className="action-icon text-red-600 hover:text-red-800"
            data-testid={`delete-settings-${setting.settings_id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

const COMPETITION_TYPES = [
  'Local',
  'Regional',
  'Area/State',
  'National',
  'International',
  'World',
  'Invitational'
];

const BLOCK_LENGTHS = [
  { value: '2:00', label: '2 hours' },
  { value: '3:00', label: '3 hours' },
  { value: '3:30', label: '3 hours 30 mins' }
];

// Time spinner component
const TimeSpinner = ({ value, onChange, label }) => {
  const [hours, minutes] = value.split(':').map(Number);
  
  const incrementHour = () => {
    const newHours = (hours + 1) % 24;
    onChange(`${newHours}:${String(minutes).padStart(2, '0')}`);
  };
  
  const decrementHour = () => {
    const newHours = (hours - 1 + 24) % 24;
    onChange(`${newHours}:${String(minutes).padStart(2, '0')}`);
  };
  
  const incrementMinute = () => {
    let newMinutes = (minutes + 15) % 60;
    let newHours = hours;
    if (minutes + 15 >= 60) {
      newHours = (hours + 1) % 24;
    }
    onChange(`${newHours}:${String(newMinutes).padStart(2, '0')}`);
  };
  
  const decrementMinute = () => {
    let newMinutes = (minutes - 15 + 60) % 60;
    let newHours = hours;
    if (minutes - 15 < 0) {
      newHours = (hours - 1 + 24) % 24;
    }
    onChange(`${newHours}:${String(newMinutes).padStart(2, '0')}`);
  };
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center border rounded-lg overflow-hidden bg-white">
        <span className="px-3 py-2 font-mono text-lg min-w-[70px] text-center">
          {hours}:{String(minutes).padStart(2, '0')}
        </span>
        <div className="flex flex-col border-l">
          <div className="flex border-b">
            <div className="flex flex-col items-center px-1">
              <span className="text-[10px] text-muted-foreground">H</span>
              <div className="flex gap-0.5">
                <button 
                  type="button"
                  onClick={decrementHour} 
                  className="px-1.5 py-0.5 hover:bg-stone-100 text-xs"
                >
                  −
                </button>
                <button 
                  type="button"
                  onClick={incrementHour} 
                  className="px-1.5 py-0.5 hover:bg-stone-100 text-xs"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex flex-col items-center px-1 border-l">
              <span className="text-[10px] text-muted-foreground">M</span>
              <div className="flex gap-0.5">
                <button 
                  type="button"
                  onClick={decrementMinute} 
                  className="px-1.5 py-0.5 hover:bg-stone-100 text-xs"
                >
                  −
                </button>
                <button 
                  type="button"
                  onClick={incrementMinute} 
                  className="px-1.5 py-0.5 hover:bg-stone-100 text-xs"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TimeBlocksTab() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showcaseHelpOpen, setShowcaseHelpOpen] = useState(false);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  
  const [formData, setFormData] = useState({
    competition_type: '',
    block_length: '3:00',
    official_practices_required: false,
    evening_showcase_events: false,
    standard_day_start: '7:00',
    standard_day_end: '22:00',
    first_day_start: '18:00',
    first_day_end: '22:00',
    final_day_start: '8:00',
    final_day_end: '15:00'
  });
  
  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/event-timings/order-of-skate-settings`, { withCredentials: true });
      setSettings(res.data || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const resetForm = () => {
    setFormData({
      competition_type: '',
      block_length: '3:00',
      official_practices_required: false,
      evening_showcase_events: false,
      standard_day_start: '7:00',
      standard_day_end: '22:00',
      first_day_start: '18:00',
      first_day_end: '22:00',
      final_day_start: '8:00',
      final_day_end: '15:00'
    });
    setEditingSettings(null);
  };
  
  const handleOpenDialog = () => {
    resetForm();
    setDialogOpen(true);
  };
  
  const handleEdit = (setting) => {
    setFormData({
      competition_type: setting.competition_type,
      block_length: setting.block_length || '3:00',
      official_practices_required: setting.official_practices_required || false,
      evening_showcase_events: setting.evening_showcase_events || false,
      standard_day_start: setting.standard_day_start || '7:00',
      standard_day_end: setting.standard_day_end || '22:00',
      first_day_start: setting.first_day_start || '18:00',
      first_day_end: setting.first_day_end || '22:00',
      final_day_start: setting.final_day_start || '8:00',
      final_day_end: setting.final_day_end || '15:00'
    });
    setEditingSettings(setting);
    setDialogOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.competition_type) {
      toast.error('Please select a competition type');
      return;
    }
    
    // Check if this competition type already exists (for new entries)
    if (!editingSettings && settings.some(s => s.competition_type === formData.competition_type)) {
      toast.error(`Settings for ${formData.competition_type} competitions already exist`);
      return;
    }
    
    try {
      if (editingSettings) {
        await axios.put(
          `${API}/event-timings/order-of-skate-settings/${editingSettings.settings_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Settings updated successfully');
      } else {
        await axios.post(
          `${API}/event-timings/order-of-skate-settings`,
          formData,
          { withCredentials: true }
        );
        toast.success('Settings created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };
  
  const handleDelete = (setting) => {
    setDeleteTarget(setting);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/event-timings/order-of-skate-settings/${deleteTarget.settings_id}`,
        { withCredentials: true }
      );
      toast.success('Settings deleted successfully');
      fetchSettings();
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Failed to delete settings');
    } finally {
      setDeleting(false);
    }
  };
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = settings.findIndex(s => s.settings_id === active.id);
      const newIndex = settings.findIndex(s => s.settings_id === over.id);
      
      const reordered = arrayMove(settings, oldIndex, newIndex);
      setSettings(reordered);
      
      // Save new order to backend
      try {
        const orderData = reordered.map((s, idx) => ({
          settings_id: s.settings_id,
          sort_order: idx
        }));
        await axios.put(
          `${API}/event-timings/order-of-skate-settings/reorder`,
          { items: orderData },
          { withCredentials: true }
        );
      } catch (error) {
        console.error('Failed to save order:', error);
        // Revert on error
        fetchSettings();
      }
    }
  };
  
  // Get available competition types (exclude already used ones when adding new)
  const getAvailableTypes = () => {
    if (editingSettings) {
      return COMPETITION_TYPES;
    }
    const usedTypes = settings.map(s => s.competition_type);
    return COMPETITION_TYPES.filter(t => !usedTypes.includes(t));
  };
  
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Order of Skate Settings</h2>
        <Button onClick={handleOpenDialog} className="btn-primary" data-testid="add-settings-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Settings
        </Button>
      </div>
      
      <Card className="p-6">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>Loading...</p>
          </div>
        ) : settings.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No settings configured yet.</p>
            <p className="text-sm mt-1">Click "+ Add Settings" to configure your first competition type.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8 py-3 px-2"></th>
                    <th className="text-left py-3 px-2 font-semibold">Settings for</th>
                    <th className="text-right py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={settings.map(s => s.settings_id)} strategy={verticalListSortingStrategy}>
                    {settings.map(setting => (
                      <SortableSettingsRow
                        key={setting.settings_id}
                        setting={setting}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </Card>
      
      {/* Add/Edit Settings Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSettings ? `Edit Settings for ${editingSettings.competition_type} Competitions` : 'Add Order of Skate Settings'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Competition Type */}
            <div>
              <Label>Competition Type *</Label>
              <select
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={formData.competition_type}
                onChange={(e) => setFormData({ ...formData, competition_type: e.target.value })}
                disabled={!!editingSettings}
                data-testid="competition-type-select"
              >
                <option value="">Select competition type</option>
                {getAvailableTypes().map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            {/* Block Length */}
            <div>
              <Label>Length of Competition Blocks</Label>
              <select
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={formData.block_length}
                onChange={(e) => setFormData({ ...formData, block_length: e.target.value })}
                data-testid="block-length-select"
              >
                {BLOCK_LENGTHS.map(bl => (
                  <option key={bl.value} value={bl.value}>{bl.label}</option>
                ))}
              </select>
            </div>
            
            {/* Checkboxes */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="official-practices"
                  checked={formData.official_practices_required}
                  onChange={(e) => setFormData({ ...formData, official_practices_required: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="official-practices-checkbox"
                />
                <label htmlFor="official-practices" className="text-sm">
                  Official Practices are required for this type of event
                </label>
                <span className="text-xs text-muted-foreground">(Tick if yes)</span>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="evening-showcase"
                  checked={formData.evening_showcase_events}
                  onChange={(e) => setFormData({ ...formData, evening_showcase_events: e.target.checked })}
                  className="h-4 w-4"
                  data-testid="evening-showcase-checkbox"
                />
                <label htmlFor="evening-showcase" className="text-sm">
                  Evening programmes should include showcase events
                </label>
                <span className="text-xs text-muted-foreground">(Tick if yes)</span>
                <button
                  type="button"
                  onClick={() => setShowcaseHelpOpen(true)}
                  className="text-muted-foreground hover:text-primary"
                  data-testid="showcase-help-btn"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* Start and Finish Times Section */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-2">Start and Finish Times</h3>
              <div className="text-sm text-muted-foreground mb-4 space-y-2">
                <p>Set the ideal daily start times for full days within the event. This will also set an ideal finish time for the first day and an ideal start time for the last day.</p>
                <p>For a single day competition, the system will take the ideal start and finish times from the standard day. In both single and multiday competitions, you'll have the chance to edit the default values if they don't work for your event.</p>
              </div>
              
              {/* Daily Times Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-sm">Day Type</th>
                      <th className="text-center py-2 px-3 font-medium text-sm">Start Time</th>
                      <th className="text-center py-2 px-3 font-medium text-sm">Finish Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="py-3 px-3 text-sm font-medium">First Day</td>
                      <td className="py-3 px-3">
                        <div className="flex justify-center">
                          <TimeSpinner
                            value={formData.first_day_start}
                            onChange={(v) => setFormData({ ...formData, first_day_start: v })}
                          />
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex justify-center">
                          <TimeSpinner
                            value={formData.first_day_end}
                            onChange={(v) => setFormData({ ...formData, first_day_end: v })}
                          />
                        </div>
                      </td>
                    </tr>
                    <tr className="border-t bg-stone-50/50">
                      <td className="py-3 px-3 text-sm font-medium">Standard Day</td>
                      <td className="py-3 px-3">
                        <div className="flex justify-center">
                          <TimeSpinner
                            value={formData.standard_day_start}
                            onChange={(v) => setFormData({ ...formData, standard_day_start: v })}
                          />
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex justify-center">
                          <TimeSpinner
                            value={formData.standard_day_end}
                            onChange={(v) => setFormData({ ...formData, standard_day_end: v })}
                          />
                        </div>
                      </td>
                    </tr>
                    <tr className="border-t">
                      <td className="py-3 px-3 text-sm font-medium">Final Day</td>
                      <td className="py-3 px-3">
                        <div className="flex justify-center">
                          <TimeSpinner
                            value={formData.final_day_start}
                            onChange={(v) => setFormData({ ...formData, final_day_start: v })}
                          />
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex justify-center">
                          <TimeSpinner
                            value={formData.final_day_end}
                            onChange={(v) => setFormData({ ...formData, final_day_end: v })}
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 btn-primary" data-testid="save-settings-btn">
                {editingSettings ? 'Update Settings' : 'Save Settings'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Showcase Help Dialog */}
      <Dialog open={showcaseHelpOpen} onOpenChange={setShowcaseHelpOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              What are showcase events?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              Showcase events are likely to have high quality and/or entertaining skating content. 
              To allow this to be more accessible to the public, these events will be prioritised 
              for evening event blocks.
            </p>
            <p>
              Examples include Senior Freeskating, Show skates and other team based events, Couples events.
            </p>
            <p>
              Users will get the chance while creating the order of skate to select which specific 
              events might be showcased, or to deactivate it for the event they are creating.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setShowcaseHelpOpen(false)} data-testid="showcase-help-ok-btn">
              Okay
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemName={deleteTarget ? `settings for ${deleteTarget.competition_type} Competitions` : ''}
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </>
  );
}
